<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import CallPermissionRequest from './CallPermissionRequest.vue';
import TemplatesPicker from './TemplatesPicker.vue';
import WhatsAppTemplateReply from './WhatsAppTemplateReply.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  sendRenderedContent: {
    type: Boolean,
    default: false,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  callPermissionEligibility: {
    type: Object,
    default: () => ({ canRequest: true }),
  },
});

const emit = defineEmits([
  'onSend',
  'cancel',
  'requestCallPermission',
  'update:show',
]);

const { t } = useI18n();
const selectedWaTemplate = ref(null);
const requestPending = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const modalHeaderContent = computed(() =>
  selectedWaTemplate.value
    ? t('WHATSAPP_TEMPLATES.MODAL.TEMPLATE_SELECTED_SUBTITLE', {
        templateName: selectedWaTemplate.value.name,
      })
    : t('WHATSAPP_TEMPLATES.MODAL.SUBTITLE')
);

const isCallPermissionTemplate = computed(() =>
  selectedWaTemplate.value?.components?.some(
    component => component?.type?.toUpperCase() === 'CALL_PERMISSION_REQUEST'
  )
);

const callPermissionRequestIsSubmitting = computed(
  () => props.isSubmitting || requestPending.value
);

const resetSelection = () => {
  selectedWaTemplate.value = null;
  requestPending.value = false;
};

const pickTemplate = template => {
  selectedWaTemplate.value = template;
};

const onSendMessage = message => {
  emit('onSend', message);
};

const onRequestCallPermission = templateParams => {
  if (callPermissionRequestIsSubmitting.value) return;

  requestPending.value = true;
  emit('requestCallPermission', templateParams);
};

const onClose = () => {
  resetSelection();
  emit('cancel');
};

watch(
  () => props.isSubmitting,
  (isSubmitting, wasSubmitting) => {
    if (wasSubmitting && !isSubmitting) requestPending.value = false;
  }
);

watch(
  () => props.show,
  show => {
    if (!show) resetSelection();
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="$t('WHATSAPP_TEMPLATES.MODAL.TITLE')"
      :header-content="modalHeaderContent"
    />
    <div class="w-full px-8 py-6">
      <TemplatesPicker
        v-if="!selectedWaTemplate"
        :inbox-id="inboxId"
        enable-call-permission-templates
        @on-select="pickTemplate"
      />
      <CallPermissionRequest
        v-else-if="isCallPermissionTemplate"
        :template="selectedWaTemplate"
        :is-submitting="callPermissionRequestIsSubmitting"
        :eligibility="callPermissionEligibility"
        @cancel="resetSelection"
        @confirm="onRequestCallPermission"
      />
      <WhatsAppTemplateReply
        v-else
        :template="selectedWaTemplate"
        :send-rendered-content="sendRenderedContent"
        @reset-template="resetSelection"
        @send-message="onSendMessage"
      />
    </div>
  </woot-modal>
</template>
